import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, AlarmClock, Flag, Plus, Trash2, ExternalLink, Star } from "lucide-react";

const inputCls = "bg-white border-steel text-charcoal h-11";
const OB_STYLES = {
  not_started: "bg-elevated border-steel text-slate-500",
  in_progress: "bg-amber-100 border-amber-300 text-amber-800",
  complete: "bg-emerald-100 border-emerald-300 text-emerald-800",
};
const OB_LABELS = { not_started: "Not Started", in_progress: "In Progress", complete: "Complete" };
const LEVELS = ["developing", "consistent", "competent"];
const LEVEL_STYLES = {
  developing: "bg-amber-400 text-black border-amber-400",
  consistent: "bg-sky-500 text-white border-sky-500",
  competent: "bg-emerald-500 text-white border-emerald-500",
};
const OBJ_STATUS_LABELS = { not_started: "Not Started", in_progress: "In Progress", completed: "Completed", further_development_required: "Further Development Required" };
const FUNDING_STATUS_LABELS = { potential: "Potential", applied: "Applied", approved: "Approved", claimed: "Claimed", received: "Received", rejected: "Rejected" };
const ANNUAL_FIELDS = [
  ["employment_sustained", "Employment Sustained"], ["qualification_achieved", "Qualification Achieved"],
  ["seat_hours", "Seat Hours"], ["attendance", "Attendance"], ["safety_performance", "Safety Performance"],
  ["customer_feedback", "Customer Feedback"], ["supervisor_feedback", "Supervisor Feedback"],
  ["pay_progression", "Pay Progression"], ["competence_status", "Competence Status"],
  ["next_career_objectives", "Next Career Objectives"],
];

function OnboardingTab({ oid, data, reload, canEdit, isManagement }) {
  const [reason, setReason] = useState("");
  const set = async (key, status) => {
    try {
      await api.post(`/staff/operators/${oid}/onboarding/${key}`, { status });
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const override = async () => {
    if (!reason.trim()) { toast.error("A reason is mandatory for a Workplace Ready override."); return; }
    try {
      await api.post(`/staff/operators/${oid}/workplace-ready-override`, { reason });
      toast.success("Workplace Ready override recorded (audit-logged).");
      setReason("");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const wr = data.workplace_ready;
  return (
    <div className="space-y-5" data-testid="onboarding-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 bg-elevated rounded-md p-4">
        <div>
          <p className="text-sm text-slate-600">All onboarding runs through Connecteam — the Academy only records completion.</p>
          {data.connecteam.url && (
            <a href={data.connecteam.url} target="_blank" rel="noreferrer" className="text-sm text-lomond font-semibold underline inline-flex items-center gap-1 mt-1" data-testid="connecteam-link">
              Open Connecteam <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          {data.connecteam.note && <p className="text-xs text-slate-500 mt-1">{data.connecteam.note}</p>}
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-mono uppercase tracking-widest ${
          wr.ready ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-red-50 border-red-300 text-red-700"
        }`} data-testid="workplace-ready-gate">
          Workplace Ready: {wr.ready ? "YES" : "NO"}
        </span>
      </div>

      {wr.override && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid="ready-override-note">
          Override by {wr.override.by_name} — {new Date(wr.override.at).toLocaleString("en-GB")} — "{wr.override.reason}"
        </p>
      )}

      <div className="space-y-2">
        {data.onboarding_items.map((i) => (
          <div key={i.key} className="bg-elevated rounded-md p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`ob-${i.key}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-charcoal">{i.label}</p>
              {i.by_name && <p className="text-[10px] text-slate-400 font-mono">{OB_LABELS[i.status]} — {i.by_name} — {new Date(i.at).toLocaleDateString("en-GB")}</p>}
            </div>
            {canEdit && (
              <div className="flex gap-1.5 shrink-0">
                {["not_started", "in_progress", "complete"].map((s) => (
                  <button key={s} type="button" onClick={() => set(i.key, s)}
                    className={`px-2.5 py-2 rounded-md border text-[10px] font-bold uppercase tracking-wide transition-colors ${
                      i.status === s ? OB_STYLES[s] + " font-black" : "bg-white text-slate-500 border-steel hover:border-lomond"
                    }`} data-testid={`ob-${i.key}-${s}`}>
                    {OB_LABELS[s]}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {!wr.ready && isManagement && (
        <div className="bg-amber-50 border border-amber-300 rounded-md p-4 space-y-2" data-testid="ready-override-box">
          <p className="text-sm text-amber-800 font-semibold">Management override — mark Workplace Ready without all items complete</p>
          <div className="flex gap-2 flex-wrap">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Mandatory reason (audit-logged)" className="bg-white border-amber-300 text-charcoal h-10 flex-1 min-w-52" data-testid="ready-override-reason" />
            <Button onClick={override} variant="outline" className="border-amber-400 text-amber-800 bg-white" data-testid="ready-override-btn">Override to YES</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PlansTab({ oid, data, reload, canEdit }) {
  const [sel, setSel] = useState(1);
  const plan = data.plans.find((p) => p.plan_number === sel);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) setForm({ objectives: JSON.parse(JSON.stringify(plan.objectives)), manager_comments: plan.manager_comments || "", next_priorities: plan.next_priorities || "", outcome: plan.outcome || "", annual_outcome: plan.annual_outcome || {} });
  }, [plan]);

  if (!plan || !form) return null;
  const setO = (i, k, v) => setForm({ ...form, objectives: form.objectives.map((o, oi) => (oi === i ? { ...o, [k]: v } : o)) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/staff/operators/${oid}/action-plans/${sel}`, form);
      toast.success(`Action Plan ${sel} saved.`);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const sign = async () => {
    if (!window.confirm(`Sign off Action Plan ${sel} (${plan.period_label})?`)) return;
    try {
      await api.post(`/staff/operators/${oid}/action-plans/${sel}/sign`);
      toast.success("Plan signed off.");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="plans-tab">
      <div className="flex gap-1.5 flex-wrap" data-testid="plan-selector">
        {data.plans.map((p) => (
          <button key={p.plan_number} type="button" onClick={() => setSel(p.plan_number)}
            className={`px-3.5 py-2 rounded-md border text-xs font-bold uppercase tracking-wide transition-colors ${
              sel === p.plan_number ? "bg-lomond text-black border-lomond" : "bg-white border-steel text-slate-600 hover:border-lomond"
            }`} data-testid={`plan-tab-${p.plan_number}`}>
            AP{p.plan_number} · {p.period_label}{p.manager_signoff ? " ✓" : ""}
          </button>
        ))}
      </div>

      <div className="bg-elevated rounded-md p-4 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="plan-dates">
        <span className="text-slate-600">Period: <span className="font-semibold text-charcoal">{plan.period_label}</span></span>
        <span className="text-slate-600">Start: <span className="font-semibold text-charcoal">{plan.start_date}</span></span>
        <span className="text-slate-600">Target review: <span className="font-semibold text-charcoal">{plan.target_review_date}</span></span>
        {plan.employee_ack && <span className="text-emerald-700 font-semibold" data-testid="plan-ack-state">Acknowledged by operator</span>}
        {plan.manager_signoff && <span className="text-emerald-700 font-semibold" data-testid="plan-sign-state">Signed off by {plan.manager_signoff.by_name} — {plan.completion_date}</span>}
      </div>

      {data.plan_experience?.[String(sel)] && (
        <div className="bg-white border border-steel rounded-md p-4" data-testid="plan-experience-summary">
          <p className="text-[10px] font-mono uppercase tracking-widest text-lomond mb-2">Verified live experience in this period</p>
          <p className="text-sm text-charcoal font-semibold">{data.plan_experience[String(sel)].seat_hours}h seat — {data.plan_experience[String(sel)].weeks_logged} week(s) logged</p>
          <div className="flex gap-1.5 flex-wrap mt-2">
            {data.plan_experience[String(sel)].machines.map((m) => <span key={m} className="text-[10px] font-mono bg-elevated border border-steel rounded-sm px-2 py-0.5">{m}</span>)}
            {data.plan_experience[String(sel)].tasks.map((t) => <span key={t} className="text-[10px] font-mono bg-elevated border border-steel rounded-sm px-2 py-0.5">{t}</span>)}
          </div>
          {data.plan_experience[String(sel)].customers.length > 0 && (
            <p className="text-xs text-slate-500 mt-2">Customers/sites: {[...data.plan_experience[String(sel)].customers, ...data.plan_experience[String(sel)].sites].join(" · ")}</p>
          )}
          {data.plan_experience[String(sel)].development_points.length > 0 && (
            <p className="text-xs text-amber-800 mt-1">Development points: {data.plan_experience[String(sel)].development_points.join(" · ")}</p>
          )}
          {data.plan_experience[String(sel)].safety_notes.length > 0 && (
            <p className="text-xs text-red-700 mt-1" data-testid="plan-experience-safety">Safety notes: {data.plan_experience[String(sel)].safety_notes.map((s) => `${s.week_ending}: ${s.note}`).join(" · ")}</p>
          )}
        </div>
      )}

      <div className="space-y-3">
        {form.objectives.map((o, i) => (
          <div key={o.id || i} className="bg-white border border-steel rounded-md p-4 space-y-2.5" data-testid={`objective-${i}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <p className="font-heading font-bold text-charcoal text-sm flex-1 min-w-48">{i + 1}. {o.title}</p>
              {canEdit ? (
                <select value={o.status} onChange={(e) => setO(i, "status", e.target.value)}
                  className="h-9 rounded-md border border-steel bg-white px-2 text-xs text-charcoal" data-testid={`objective-status-${i}`}>
                  {data.objective_statuses.map((s) => <option key={s} value={s}>{OBJ_STATUS_LABELS[s]}</option>)}
                </select>
              ) : (
                <span className="text-xs font-mono uppercase text-slate-500">{OBJ_STATUS_LABELS[o.status]}</span>
              )}
            </div>
            {o.auto_evidence && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2" data-testid={`objective-auto-evidence-${i}`}>Auto evidence: {o.auto_evidence}</p>}
            {canEdit ? (
              <div className="grid sm:grid-cols-2 gap-2.5">
                <Input value={o.target || ""} onChange={(e) => setO(i, "target", e.target.value)} placeholder="Target" className={inputCls} data-testid={`objective-target-${i}`} />
                <Input value={o.support || ""} onChange={(e) => setO(i, "support", e.target.value)} placeholder="Support provided by Lomond" className={inputCls} data-testid={`objective-support-${i}`} />
                <Input value={o.employee_action || ""} onChange={(e) => setO(i, "employee_action", e.target.value)} placeholder="Employee action" className={inputCls} data-testid={`objective-action-${i}`} />
                <Input value={o.progress || ""} onChange={(e) => setO(i, "progress", e.target.value)} placeholder="Progress" className={inputCls} data-testid={`objective-progress-${i}`} />
                <Input value={o.evidence || ""} onChange={(e) => setO(i, "evidence", e.target.value)} placeholder="Evidence" className={`${inputCls} sm:col-span-2`} data-testid={`objective-evidence-${i}`} />
              </div>
            ) : (
              <div className="text-xs text-slate-600 space-y-1">
                {o.target && <p><span className="font-semibold">Target:</span> {o.target}</p>}
                {o.progress && <p><span className="font-semibold">Progress:</span> {o.progress}</p>}
                {o.evidence && <p><span className="font-semibold">Evidence:</span> {o.evidence}</p>}
              </div>
            )}
          </div>
        ))}
      </div>

      {sel === 3 && canEdit && (
        <div className="bg-elevated rounded-md p-4 space-y-2" data-testid="plan3-outcome">
          <label className="text-sm font-semibold text-charcoal">Period outcome</label>
          <select value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })}
            className="h-11 rounded-md border border-steel bg-white px-3 text-sm text-charcoal w-full sm:w-auto" data-testid="plan3-outcome-select">
            <option value="">— not decided —</option>
            <option value="continue_development">Continue Development</option>
            <option value="ready_for_competence_review">Ready for Lomond Competence Review</option>
          </select>
        </div>
      )}

      {sel === 4 && (
        <div className="bg-elevated rounded-md p-4 space-y-2.5" data-testid="annual-outcome">
          <p className="font-heading font-bold uppercase text-charcoal text-sm">Annual Operator Development Outcome (Week 52)</p>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {ANNUAL_FIELDS.map(([k, l]) => (
              <div key={k} className="space-y-1">
                <label className="text-xs text-slate-500">{l}</label>
                <Input value={form.annual_outcome[k] || ""} disabled={!canEdit}
                  onChange={(e) => setForm({ ...form, annual_outcome: { ...form.annual_outcome, [k]: e.target.value } })}
                  className={inputCls} data-testid={`annual-${k}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-charcoal">Manager comments</label>
          <Textarea value={form.manager_comments} disabled={!canEdit} onChange={(e) => setForm({ ...form, manager_comments: e.target.value })}
            className="bg-white border-steel text-charcoal" data-testid="plan-manager-comments" />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-charcoal">Next development priorities</label>
          <Textarea value={form.next_priorities} disabled={!canEdit} onChange={(e) => setForm({ ...form, next_priorities: e.target.value })}
            className="bg-white border-steel text-charcoal" data-testid="plan-next-priorities" />
        </div>
      </div>
      {plan.employee_comments && <p className="text-sm text-slate-600"><span className="font-semibold text-charcoal">Operator comments:</span> {plan.employee_comments}</p>}

      {canEdit && (
        <div className="flex gap-2 flex-wrap">
          <Button onClick={save} disabled={saving} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid={`plan-save-${sel}`}>
            {saving ? "Saving..." : `Save Action Plan ${sel}`}
          </Button>
          {!plan.manager_signoff && (
            <Button onClick={sign} variant="outline" className="border-emerald-500 text-emerald-700 bg-white" data-testid={`plan-sign-${sel}`}>
              Manager Sign-Off
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function ExperienceTab({ oid, data, reload, canEdit, isManagement }) {
  const blank = { customer: "", site: "", start_date: "", end_date: "", machine: "", machine_category: "", seat_hours: "", work_type: "General Civils", supervisor: "", override_reason: "" };
  const expBlank = { week_ending: "", date_from: "", date_to: "", customer: "", site: "", machine_category: "", machine_size: "", seat_hours: "", tasks: [] };
  const [form, setForm] = useState(blank);
  const [expForm, setExpForm] = useState(expBlank);
  const [fbFor, setFbFor] = useState(null);
  const [fb, setFb] = useState({ scores: {}, comments: "" });
  const [reviewFor, setReviewFor] = useState(null);
  const [review, setReview] = useState({ hours_reasonable: false, description_confirmed: false, exposure_confirmed: false, ratings: {}, comments: "", strengths: "", development_points: "" });
  const [returnFor, setReturnFor] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [reopenFor, setReopenFor] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [saving, setSaving] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setE = (k, v) => setExpForm({ ...expForm, [k]: v });
  const expRecords = (data.experience && data.experience.records) || [];

  const EXP_STYLES = {
    draft: "bg-elevated border-steel text-slate-500",
    submitted: "bg-amber-100 border-amber-300 text-amber-800",
    verified: "bg-emerald-100 border-emerald-300 text-emerald-800",
    returned: "bg-red-100 border-red-300 text-red-700",
  };

  const add = async () => {
    setSaving("add");
    try {
      await api.post(`/staff/operators/${oid}/assignments`, { ...form, seat_hours: parseFloat(form.seat_hours) || 0 });
      toast.success("Assignment recorded.");
      setForm(blank);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const addExp = async () => {
    setSaving("expadd");
    try {
      await api.post(`/staff/operators/${oid}/experience`, { ...expForm, seat_hours: parseFloat(expForm.seat_hours) || 0 });
      toast.success("Live experience record added (verified).");
      setExpForm(expBlank);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const verify = async (eid) => {
    setSaving(`verify-${eid}`);
    try {
      await api.post(`/staff/operators/${oid}/experience/${eid}/verify`, review);
      toast.success("Record verified.");
      setReviewFor(null);
      setReview({ hours_reasonable: false, description_confirmed: false, exposure_confirmed: false, ratings: {}, comments: "", strengths: "", development_points: "" });
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const returnRec = async (eid) => {
    if (!returnReason.trim()) { toast.error("A reason is required."); return; }
    setSaving(`return-${eid}`);
    try {
      await api.post(`/staff/operators/${oid}/experience/${eid}/return`, { reason: returnReason });
      toast.success("Returned to operator for amendment.");
      setReturnFor(null); setReturnReason("");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const reopen = async (eid) => {
    if (!reopenReason.trim()) { toast.error("A reason is required to reopen."); return; }
    setSaving(`reopen-${eid}`);
    try {
      await api.post(`/staff/operators/${oid}/experience/${eid}/reopen`, { reason: reopenReason });
      toast.success("Record reopened — operator can amend and resubmit.");
      setReopenFor(null); setReopenReason("");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const saveFeedback = async (aid) => {
    setSaving(aid);
    try {
      await api.post(`/staff/operators/${oid}/assignments/${aid}/feedback`, fb);
      toast.success("Feedback saved.");
      setFbFor(null);
      setFb({ scores: {}, comments: "" });
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  return (
    <div className="space-y-5" data-testid="experience-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-charcoal text-white rounded-sm p-3.5 text-center col-span-2 sm:col-span-1">
          <p className="font-heading font-black text-2xl text-[#FFC800]" data-testid="seat-total">{data.seat_hours.total}</p>
          <p className="text-[9px] font-mono uppercase tracking-widest text-white/50 mt-1">Total development seat hours</p>
        </div>
        {Object.entries(data.seat_hours.by_category).map(([cat, h]) => (
          <div key={cat} className="bg-elevated rounded-sm p-3.5 text-center">
            <p className="font-heading font-black text-xl text-charcoal">{h}h</p>
            <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">{cat}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500">Seat hours are development evidence — not an automatic competence trigger. Daily timesheets live in Connecteam.</p>

      <div className="space-y-3" data-testid="operator-experience-records">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="font-heading font-bold uppercase text-charcoal text-sm">Operator-submitted live experience</p>
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Operator records the week — Lomond verifies</span>
        </div>
        {expRecords.map((r) => (
          <div key={r.id} className="bg-white border border-steel rounded-md p-4 space-y-2.5" data-testid={`exp-admin-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-charcoal">Week ending {r.week_ending} — {r.customer}{r.site ? ` — ${r.site}` : ""}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {r.date_from}{r.date_to ? ` → ${r.date_to}` : ""} — {[r.machine_category, r.machine_size].filter(Boolean).join(" ") || "machine not recorded"}
                  {r.location ? ` — ${r.location}` : ""}{r.supervisor ? ` — Site contact: ${r.supervisor}` : ""}
                </p>
                {r.tasks?.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap mt-1.5">
                    {r.tasks.map((t) => <span key={t} className="text-[10px] font-mono bg-elevated border border-steel rounded-sm px-2 py-0.5">{t}</span>)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono font-bold text-charcoal text-sm">{r.seat_hours}h seat</span>
                <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${EXP_STYLES[r.status]}`} data-testid={`exp-admin-status-${r.id}`}>{r.status}</span>
              </div>
            </div>
            {Object.entries(r.reflection || {}).filter(([, v]) => v).length > 0 && (
              <div className="text-xs text-slate-600 space-y-0.5" data-testid={`exp-admin-reflection-${r.id}`}>
                {Object.entries(r.reflection).filter(([, v]) => v).map(([k, v]) => (
                  <p key={k}><span className="font-semibold text-charcoal capitalize">{k}:</span> {v}</p>
                ))}
              </div>
            )}
            {r.review && (
              <div className="bg-elevated rounded-sm p-3" data-testid={`exp-admin-review-${r.id}`}>
                <div className="flex gap-1.5 flex-wrap">
                  {Object.entries(r.review.ratings || {}).map(([f, v]) => (
                    <span key={f} className="text-[10px] font-mono bg-white border border-steel rounded-sm px-2 py-0.5">{f.replace(/_/g, " ")} <strong>{v}/5</strong></span>
                  ))}
                </div>
                {r.review.comments && <p className="text-xs text-slate-600 mt-1">"{r.review.comments}" — {r.review.by_name}</p>}
                {r.review.development_points && <p className="text-xs text-amber-800 mt-1"><strong>Development points:</strong> {r.review.development_points}</p>}
                {r.review.strengths && <p className="text-xs text-emerald-800 mt-1"><strong>Strengths:</strong> {r.review.strengths}</p>}
              </div>
            )}
            {r.return_reason && r.status === "returned" && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-2.5 py-1.5" data-testid={`exp-admin-returned-${r.id}`}>Returned: {r.return_reason}</p>
            )}
            {canEdit && r.status === "submitted" && (
              <div className="flex gap-2 flex-wrap pt-1">
                <Button size="sm" onClick={() => { setReviewFor(reviewFor === r.id ? null : r.id); setReturnFor(null); }} className="bg-emerald-600 text-white font-bold hover:bg-emerald-700" data-testid={`exp-review-open-${r.id}`}>Review & Verify</Button>
                <Button size="sm" variant="outline" onClick={() => { setReturnFor(returnFor === r.id ? null : r.id); setReviewFor(null); }} className="border-red-300 text-red-700 bg-white" data-testid={`exp-return-open-${r.id}`}>Return for Amendment</Button>
              </div>
            )}
            {canEdit && isManagement && r.status === "verified" && (
              <div className="pt-1">
                <Button size="sm" variant="outline" onClick={() => setReopenFor(reopenFor === r.id ? null : r.id)} className="border-steel text-charcoal bg-white" data-testid={`exp-reopen-open-${r.id}`}>Reopen Record</Button>
              </div>
            )}
            {reviewFor === r.id && (
              <div className="bg-elevated rounded-md p-3.5 space-y-3" data-testid={`exp-review-form-${r.id}`}>
                <p className="text-xs font-semibold text-charcoal">Review checks (optional)</p>
                <div className="flex gap-4 flex-wrap">
                  {[["hours_reasonable", "Hours appear reasonable"], ["description_confirmed", "Work description confirmed"], ["exposure_confirmed", "Machine/task exposure confirmed"]].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={review[k]} onChange={(e) => setReview({ ...review, [k]: e.target.checked })} data-testid={`exp-check-${r.id}-${k}`} className="h-4 w-4" /> {l}
                    </label>
                  ))}
                </div>
                <p className="text-xs font-semibold text-charcoal">Supervisor ratings (optional, 1 = Needs Significant Development … 5 = Strong)</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {data.feedback_fields.map((f) => (
                    <div key={f} className="space-y-1">
                      <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{f.replace(/_/g, " ")}</label>
                      <select value={review.ratings[f] || ""} onChange={(e) => setReview({ ...review, ratings: { ...review.ratings, [f]: parseInt(e.target.value, 10) } })}
                        className="h-9 w-full rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid={`exp-rate-${r.id}-${f}`}>
                        <option value="">—</option>
                        {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <Input value={review.comments} onChange={(e) => setReview({ ...review, comments: e.target.value })} placeholder="Supervisor comments" className={inputCls} data-testid={`exp-comments-${r.id}`} />
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input value={review.strengths} onChange={(e) => setReview({ ...review, strengths: e.target.value })} placeholder="Strengths" className={inputCls} data-testid={`exp-strengths-${r.id}`} />
                  <Input value={review.development_points} onChange={(e) => setReview({ ...review, development_points: e.target.value })} placeholder="Development points" className={inputCls} data-testid={`exp-devpoints-${r.id}`} />
                </div>
                <Button size="sm" onClick={() => verify(r.id)} disabled={saving === `verify-${r.id}`} className="bg-emerald-600 text-white font-bold hover:bg-emerald-700" data-testid={`exp-verify-btn-${r.id}`}>
                  {saving === `verify-${r.id}` ? "Verifying…" : "Verify Record"}
                </Button>
              </div>
            )}
            {returnFor === r.id && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-2" data-testid={`exp-return-form-${r.id}`}>
                <Input value={returnReason} onChange={(e) => setReturnReason(e.target.value)} placeholder="Reason for returning (required — shown to operator)" className="bg-white border-red-300 text-charcoal h-10" data-testid={`exp-return-reason-${r.id}`} />
                <Button size="sm" onClick={() => returnRec(r.id)} disabled={saving === `return-${r.id}`} className="bg-red-600 text-white font-bold hover:bg-red-700" data-testid={`exp-return-btn-${r.id}`}>Return to Operator</Button>
              </div>
            )}
            {reopenFor === r.id && (
              <div className="bg-amber-50 border border-amber-300 rounded-md p-3 space-y-2" data-testid={`exp-reopen-form-${r.id}`}>
                <Input value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="Reason for reopening (required, audit-logged)" className="bg-white border-amber-300 text-charcoal h-10" data-testid={`exp-reopen-reason-${r.id}`} />
                <Button size="sm" onClick={() => reopen(r.id)} disabled={saving === `reopen-${r.id}`} className="bg-amber-500 text-black font-bold hover:bg-amber-600" data-testid={`exp-reopen-btn-${r.id}`}>Reopen</Button>
              </div>
            )}
          </div>
        ))}
        {!expRecords.length && <p className="text-sm text-slate-500">No operator-submitted experience yet.</p>}
      </div>

      {canEdit && (
        <div className="bg-white border border-steel rounded-md p-4 space-y-3" data-testid="staff-experience-form">
          <p className="font-heading font-bold uppercase text-charcoal text-sm">Add live experience record (staff — created verified)</p>
          <div className="grid sm:grid-cols-3 gap-2.5">
            <div className="space-y-1"><label className="text-xs text-slate-500">Week ending</label><Input type="date" value={expForm.week_ending} onChange={(e) => setE("week_ending", e.target.value)} className={inputCls} data-testid="sexp-week-ending" /></div>
            <div className="space-y-1"><label className="text-xs text-slate-500">From</label><Input type="date" value={expForm.date_from} onChange={(e) => setE("date_from", e.target.value)} className={inputCls} data-testid="sexp-date-from" /></div>
            <div className="space-y-1"><label className="text-xs text-slate-500">To</label><Input type="date" value={expForm.date_to} onChange={(e) => setE("date_to", e.target.value)} className={inputCls} data-testid="sexp-date-to" /></div>
            <Input value={expForm.customer} onChange={(e) => setE("customer", e.target.value)} placeholder="Customer (required)" className={inputCls} data-testid="sexp-customer" />
            <Input value={expForm.site} onChange={(e) => setE("site", e.target.value)} placeholder="Site" className={inputCls} data-testid="sexp-site" />
            <Input type="number" value={expForm.seat_hours} onChange={(e) => setE("seat_hours", e.target.value)} placeholder="Seat hours" className={inputCls} data-testid="sexp-seat-hours" />
            <select value={expForm.machine_category} onChange={(e) => setE("machine_category", e.target.value)} className="h-11 rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid="sexp-machine-category">
              <option value="">Machine category…</option>
              {(data.machine_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={expForm.machine_size} onChange={(e) => setE("machine_size", e.target.value)} className="h-11 rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid="sexp-machine-size">
              <option value="">Machine size…</option>
              {(data.machine_sizes || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="sexp-tasks">
            {(data.task_tags || []).map((t) => (
              <button key={t} type="button" onClick={() => setE("tasks", expForm.tasks.includes(t) ? expForm.tasks.filter((x) => x !== t) : [...expForm.tasks, t])}
                className={`px-2.5 py-1.5 rounded-sm border text-[10px] font-mono transition-colors ${expForm.tasks.includes(t) ? "bg-lomond text-black border-lomond font-bold" : "border-steel text-slate-600 hover:border-lomond"}`}
                data-testid={`sexp-task-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {t}
              </button>
            ))}
          </div>
          <Button onClick={addExp} disabled={saving === "expadd"} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="sexp-add-btn">
            {saving === "expadd" ? "Saving…" : "Add Record"}
          </Button>
        </div>
      )}

      {canEdit && (
        <div className="bg-white border border-steel rounded-md p-4 space-y-3" data-testid="assignment-form">
          <p className="font-heading font-bold uppercase text-charcoal text-sm">Record assignment</p>
          <div className="grid sm:grid-cols-3 gap-2.5">
            <Input value={form.customer} onChange={(e) => set("customer", e.target.value)} placeholder="Customer (required)" className={inputCls} data-testid="as-customer" />
            <Input value={form.site} onChange={(e) => set("site", e.target.value)} placeholder="Site" className={inputCls} data-testid="as-site" />
            <select value={form.work_type} onChange={(e) => set("work_type", e.target.value)} className="h-11 rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid="as-work-type">
              {data.work_types.map((w) => <option key={w}>{w}</option>)}
            </select>
            <div className="space-y-1"><label className="text-xs text-slate-500">Start</label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} data-testid="as-start" /></div>
            <div className="space-y-1"><label className="text-xs text-slate-500">End</label><Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={inputCls} data-testid="as-end" /></div>
            <Input value={form.supervisor} onChange={(e) => set("supervisor", e.target.value)} placeholder="Supervisor" className={inputCls} data-testid="as-supervisor" />
            <Input value={form.machine} onChange={(e) => set("machine", e.target.value)} placeholder="Machine (e.g. CAT 320)" className={inputCls} data-testid="as-machine" />
            <Input value={form.machine_category} onChange={(e) => set("machine_category", e.target.value)} placeholder="Category (e.g. 360 >10t)" className={inputCls} data-testid="as-category" />
            <Input type="number" value={form.seat_hours} onChange={(e) => set("seat_hours", e.target.value)} placeholder="Approx seat hours" className={inputCls} data-testid="as-hours" />
          </div>
          {!data.workplace_ready.ready && (
            <div className="bg-amber-50 border border-amber-300 rounded-md p-3">
              <p className="text-xs text-amber-800 font-semibold mb-2">Not Workplace Ready yet — management may override with a reason</p>
              <Input value={form.override_reason} onChange={(e) => set("override_reason", e.target.value)} placeholder="Override reason (management only, audit-logged)" className="bg-white border-amber-300 text-charcoal h-10" data-testid="as-override-reason" />
            </div>
          )}
          <Button onClick={add} disabled={saving === "add"} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="as-add-btn">
            {saving === "add" ? "Saving..." : "Add Assignment"}
          </Button>
        </div>
      )}

      <div className="space-y-3" data-testid="assignments-list">
        {data.assignments.map((a) => (
          <div key={a.id} className="bg-elevated rounded-md p-4" data-testid={`assignment-${a.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-charcoal">{a.customer}{a.site ? ` — ${a.site}` : ""}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {a.start_date}{a.end_date ? ` → ${a.end_date}` : ""} — {a.work_type}{a.machine ? ` — ${a.machine}` : ""}{a.machine_category ? ` (${a.machine_category})` : ""}{a.supervisor ? ` — Supervisor: ${a.supervisor}` : ""}
                </p>
                {a.ready_override && <p className="text-[10px] text-amber-700 font-mono mt-1">Workplace-ready override: {a.ready_override}</p>}
              </div>
              <span className="font-mono font-bold text-charcoal">{a.seat_hours}h</span>
            </div>
            {a.feedback ? (
              <div className="mt-3 bg-white border border-steel rounded-sm p-3" data-testid={`feedback-${a.id}`}>
                <div className="flex gap-2 flex-wrap mb-1.5">
                  {Object.entries(a.feedback.scores || {}).map(([f, v]) => (
                    <span key={f} className="text-[10px] font-mono bg-elevated border border-steel rounded-sm px-2 py-0.5">{f.replace(/_/g, " ")} <strong>{v}/5</strong></span>
                  ))}
                </div>
                {a.feedback.comments && <p className="text-xs text-slate-600">"{a.feedback.comments}" — {a.feedback.by_name}</p>}
              </div>
            ) : canEdit && (
              <div className="mt-3">
                {fbFor === a.id ? (
                  <div className="bg-white border border-steel rounded-sm p-3 space-y-2.5" data-testid={`feedback-form-${a.id}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {data.feedback_fields.map((f) => (
                        <div key={f} className="space-y-1">
                          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{f.replace(/_/g, " ")}</label>
                          <select value={fb.scores[f] || ""} onChange={(e) => setFb({ ...fb, scores: { ...fb.scores, [f]: parseInt(e.target.value, 10) } })}
                            className="h-9 w-full rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid={`fb-${a.id}-${f}`}>
                            <option value="">—</option>
                            {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                    <Input value={fb.comments} onChange={(e) => setFb({ ...fb, comments: e.target.value })} placeholder="Comments" className={inputCls} data-testid={`fb-${a.id}-comments`} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveFeedback(a.id)} disabled={saving === a.id} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid={`fb-save-${a.id}`}>Save Feedback</Button>
                      <Button size="sm" variant="outline" onClick={() => setFbFor(null)} className="border-steel text-charcoal bg-white">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setFbFor(a.id)} className="border-steel text-charcoal bg-white" data-testid={`fb-open-${a.id}`}>
                    <Star className="h-3.5 w-3.5 mr-1" /> Add Supervisor / Customer Feedback
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
        {!data.assignments.length && <p className="text-sm text-slate-500">No live assignments recorded yet.</p>}
      </div>
    </div>
  );
}

function FeedbackTab({ data }) {
  return (
    <div className="space-y-5" data-testid="feedback-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(data.feedback.averages).map(([f, v]) => (
          <div key={f} className="bg-elevated rounded-sm p-3.5 text-center" data-testid={`fb-avg-${f}`}>
            <p className="font-heading font-black text-2xl text-charcoal">{v}</p>
            <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">{f.replace(/_/g, " ")}</p>
          </div>
        ))}
        {!Object.keys(data.feedback.averages).length && <p className="text-sm text-slate-500 col-span-4">No feedback recorded yet.</p>}
      </div>
      <div className="space-y-2.5">
        {data.feedback.records.map((r, i) => (
          <div key={i} className="bg-elevated rounded-md p-3.5" data-testid={`fb-record-${i}`}>
            <p className="text-sm font-semibold text-charcoal">{r.customer} — by {r.by_name} — {new Date(r.at).toLocaleDateString("en-GB")}</p>
            <div className="flex gap-2 flex-wrap mt-1.5">
              {Object.entries(r.scores || {}).map(([f, v]) => (
                <span key={f} className="text-[10px] font-mono bg-white border border-steel rounded-sm px-2 py-0.5">{f.replace(/_/g, " ")} <strong>{v}/5</strong></span>
              ))}
            </div>
            {r.comments && <p className="text-xs text-slate-600 mt-1.5">"{r.comments}"</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function FundingTab({ oid, data, reload, canEditFunding }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(data.funding || {}); }, [data]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const money = [["eri_approved", "ERI approved amount £"], ["eri_received", "ERI amount received £"], ["training_grant", "Training grant £"],
    ["external_training_cost", "External training cost £"], ["instructor_allocation", "Instructor allocation £"],
    ["ppe_cost", "PPE cost £"], ["medical_cost", "Medical cost £"], ["other_training_cost", "Other training cost £"]];
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f };
      for (const [k] of money) payload[k] = parseFloat(payload[k]) || 0;
      if (typeof payload.eri_claim_dates === "string") payload.eri_claim_dates = payload.eri_claim_dates.split(",").map((x) => x.trim()).filter(Boolean);
      await api.put(`/staff/operators/${oid}/funding`, payload);
      toast.success("Funding data saved.");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="funding-tab">
      <p className="text-xs text-slate-500">Data capture only — the full funding dashboard comes later. Approved or potential funding is not received cash.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-sm text-slate-600">Employer / local authority funding scheme</label>
          <Input value={f.scheme || ""} onChange={(e) => set("scheme", e.target.value)} disabled={!canEditFunding} className={inputCls} data-testid="fund-scheme" />
        </div>
        {money.map(([k, l]) => (
          <div key={k} className="space-y-1.5">
            <label className="text-sm text-slate-600">{l}</label>
            <Input type="number" value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} disabled={!canEditFunding} className={inputCls} data-testid={`fund-${k}`} />
          </div>
        ))}
        <div className="space-y-1.5">
          <label className="text-sm text-slate-600">ERI claim dates (comma separated)</label>
          <Input value={Array.isArray(f.eri_claim_dates) ? f.eri_claim_dates.join(", ") : f.eri_claim_dates || ""} onChange={(e) => set("eri_claim_dates", e.target.value)} disabled={!canEditFunding} className={inputCls} data-testid="fund-claim-dates" />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm text-slate-600">Funding status</label>
          <select value={f.funding_status || "potential"} onChange={(e) => set("funding_status", e.target.value)} disabled={!canEditFunding}
            className="h-11 w-full rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid="fund-status">
            {data.funding_statuses.map((s) => <option key={s} value={s}>{FUNDING_STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm text-slate-600">Net training cost (auto)</label>
          <Input value={f.net_training_cost ?? ""} disabled className="bg-elevated border-steel text-charcoal h-11" data-testid="fund-net" />
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="text-sm text-slate-600">Notes</label>
        <Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} disabled={!canEditFunding} className="bg-white border-steel text-charcoal" data-testid="fund-notes" />
      </div>
      {canEditFunding && (
        <Button onClick={save} disabled={saving} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="fund-save-btn">
          {saving ? "Saving..." : "Save Funding Data"}
        </Button>
      )}
    </div>
  );
}

export default function OperatorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["management_admin", "instructor"].includes(user?.role);
  const isManagement = user?.role === "management_admin";
  const canEditFunding = ["management_admin", "funding_admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [rrNotes, setRrNotes] = useState("");

  const load = () => api.get(`/staff/operators/${id}`).then((r) => { setData(r.data); setStartDate(r.data.employment_start_date); }).catch(() => toast.error("Could not load operator"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="operator-profile-loading" />;
  const o = data.operator;

  const saveStartDate = async () => {
    try {
      await api.put(`/staff/operators/${id}/profile`, { employment_start_date: startDate });
      toast.success("Employment start date updated — action plan dates shifted.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleReadyReview = async () => {
    const flag = !(data.ready_for_review && data.ready_for_review.flag);
    try {
      await api.post(`/staff/operators/${id}/ready-for-review`, { flag, notes: rrNotes });
      toast.success(flag ? "Flagged ready for competence review." : "Flag cleared.");
      setRrNotes("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setCompetence = async (area, level) => {
    try {
      await api.post(`/staff/operators/${id}/competence`, { area, level });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="operator-profile-page">
      <Button variant="outline" size="sm" onClick={() => navigate("/staff/operators")} className="border-steel text-charcoal bg-white" data-testid="back-to-operators">
        <ArrowLeft className="h-4 w-4 mr-1" /> Operators
      </Button>

      <section className="bg-charcoal text-white rounded-md p-6" data-testid="operator-header">
        <div className="hazard-stripes h-1.5 w-20 mb-3 rounded-sm" />
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-xl sm:text-2xl" data-testid="operator-name">{o.name}</h1>
        <p className="text-white/60 text-xs font-mono mt-1">
          {o.operator_grade === "development" ? "Qualified Development Operator" : o.operator_grade || "Operator"} — Week {data.weeks_employed} — started {data.employment_start_date}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-center">
          {[["Onboarding", `${data.onboarding_items.filter((i) => i.status === "complete").length}/10`],
            ["Workplace Ready", data.workplace_ready.ready ? "YES" : "NO"],
            ["Seat hours", data.seat_hours.total],
            ["Assignments", data.assignments.length]].map(([l, v]) => (
            <div key={l} className="bg-white/10 rounded-sm p-2.5">
              <p className="font-heading font-black text-xl text-[#FFC800]">{v}</p>
              <p className="text-[9px] font-mono uppercase tracking-widest text-white/50">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {data.reminders.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-md p-4 space-y-1" data-testid="operator-reminders">
          {data.reminders.map((r, i) => (
            <p key={i} className="text-sm text-amber-800 flex items-center gap-2"><AlarmClock className="h-4 w-4 shrink-0" /> {r.message}</p>
          ))}
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-steel flex flex-wrap h-auto gap-0.5 p-1">
          {[["overview", "Overview"], ["onboarding", "Workplace Onboarding"], ["plans", "Action Plans"], ["experience", "Live Experience"], ["feedback", "Feedback"], ["quals", "Qualifications"], ["funding", "Funding"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="text-xs" data-testid={`tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-5 mt-4" data-testid="overview-tab">
            <section className="bg-white border border-steel rounded-md p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4 items-end">
                <div className="space-y-1.5">
                  <label className="text-sm text-slate-600">Employment start date (drives action plan periods &amp; reminders)</label>
                  <div className="flex gap-2">
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={!isManagement} className={inputCls} data-testid="employment-start-input" />
                    {isManagement && <Button onClick={saveStartDate} variant="outline" className="border-steel text-charcoal bg-white" data-testid="employment-start-save">Save</Button>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm text-slate-600">Competence review</label>
                  {data.ready_for_review?.flag ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border bg-emerald-100 border-emerald-300 text-emerald-800 text-xs font-mono uppercase tracking-widest" data-testid="ready-review-flag">
                        <Flag className="h-3.5 w-3.5" /> Ready for competence review — {data.ready_for_review.by_name}
                      </span>
                      {isManagement && <Button size="sm" variant="outline" onClick={toggleReadyReview} className="border-steel text-charcoal bg-white" data-testid="ready-review-clear">Clear flag</Button>}
                    </div>
                  ) : isManagement ? (
                    <div className="flex gap-2 flex-wrap">
                      <Input value={rrNotes} onChange={(e) => setRrNotes(e.target.value)} placeholder="Notes (optional)" className={`${inputCls} flex-1 min-w-40`} data-testid="ready-review-notes" />
                      <Button onClick={toggleReadyReview} variant="outline" className="border-emerald-500 text-emerald-700 bg-white" data-testid="ready-review-btn">Flag Ready for Review</Button>
                    </div>
                  ) : <p className="text-xs text-slate-400">Not flagged. Grade does not change automatically.</p>}
                </div>
              </div>
            </section>

            <section className="bg-white border-2 border-lomond rounded-md p-5" data-testid="review-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm">Competence Review</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {data.review?.in_progress ? "Review in progress — resume the evidence pack." :
                     data.review?.last?.decision === "competent" ? `Approved Lomond Competent Operator — ${data.review.last.decision_date}` :
                     data.review?.last?.decision === "continue_development" ? `Continuing development — next review ${data.review.last.next_review_date}` :
                     data.ready_for_review?.flag ? "Flagged ready for competence review." :
                     "Flag ready-for-review above when the evidence supports it."}
                  </p>
                </div>
                <Button onClick={() => navigate(`/staff/operators/${id}/review`)} variant="outline"
                  className="border-lomond text-charcoal bg-white hover:bg-lomond/10" data-testid="open-review-btn">
                  {data.review?.in_progress ? "Resume Review" : "Open Competence Review"}
                </Button>
              </div>
            </section>

            <section className="bg-white border border-steel rounded-md p-5" data-testid="competence-editor">
              <h3 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm mb-3">Competence Progress</h3>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {data.competence.map((c) => (
                  <div key={c.key} className="bg-elevated rounded-md p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`comp-${c.key}`}>
                    <span className="text-sm font-semibold text-charcoal">{c.label}</span>
                    <div className="flex gap-1.5">
                      {LEVELS.map((l) => (
                        <button key={l} type="button" disabled={!canEdit} onClick={() => setCompetence(c.key, l)}
                          className={`px-2.5 py-1.5 rounded-md border text-[10px] font-bold uppercase tracking-wide transition-colors ${
                            c.level === l ? LEVEL_STYLES[l] : "bg-white text-slate-500 border-steel hover:border-lomond"
                          }`} data-testid={`comp-${c.key}-${l}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3">Never auto-progressed by time or seat hours — levels are set by staff on evidence.</p>
            </section>
          </div>
        </TabsContent>

        <TabsContent value="onboarding"><div className="mt-4"><OnboardingTab oid={id} data={data} reload={load} canEdit={canEdit} isManagement={isManagement} /></div></TabsContent>
        <TabsContent value="plans"><div className="mt-4"><PlansTab oid={id} data={data} reload={load} canEdit={canEdit} /></div></TabsContent>
        <TabsContent value="experience"><div className="mt-4"><ExperienceTab oid={id} data={data} reload={load} canEdit={canEdit} isManagement={isManagement} /></div></TabsContent>
        <TabsContent value="feedback"><div className="mt-4"><FeedbackTab data={data} /></div></TabsContent>

        <TabsContent value="quals">
          <div className="mt-4 space-y-4" data-testid="quals-tab">
            {data.external ? (
              <section className="bg-white border border-steel rounded-md p-5 space-y-2">
                <h3 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm">External 360 Qualification</h3>
                <p className="text-sm text-slate-600">{data.external.course_name} — {data.external.provider}</p>
                <p className="text-sm text-slate-600">{data.external.start_date} → {data.external.end_date} — Result: <span className="font-bold text-emerald-700">{(data.external.result || "pending").toUpperCase()}</span></p>
                {data.external.certificate_url && <a href={fileUrl(data.external.certificate_url)} target="_blank" rel="noreferrer" className="text-sm text-lomond underline font-semibold" data-testid="quals-certificate">View certificate / card</a>}
                {data.external.approval && <p className="text-xs text-slate-500">Approved by {data.external.approval.by_name} — {new Date(data.external.approval.at).toLocaleString("en-GB")}</p>}
              </section>
            ) : <p className="text-sm text-slate-500">No external training record.</p>}
            {data.training_summary && (
              <section className="bg-white border border-steel rounded-md p-5 space-y-2" data-testid="quals-training">
                <h3 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm">Training Camp Summary</h3>
                <p className="text-sm text-slate-600">Modules completed: {data.training_summary.modules_completed}/10</p>
                {data.training_summary.final_theory && <p className="text-sm text-slate-600">Foundation theory: {data.training_summary.final_theory.score_pct}% — {data.training_summary.final_theory.status.replace(/_/g, " ")}</p>}
                {data.training_summary.final_practical_overall && <p className="text-sm text-slate-600">Foundation practical: {data.training_summary.final_practical_overall.replace(/_/g, " ")}</p>}
                {data.training_summary.gate && <p className="text-sm text-slate-600">Provider gate: {data.training_summary.gate}</p>}
              </section>
            )}
          </div>
        </TabsContent>

        <TabsContent value="funding"><div className="mt-4"><FundingTab oid={id} data={data} reload={load} canEditFunding={canEditFunding} /></div></TabsContent>
      </Tabs>
    </div>
  );
}
